import {
  ADJUVANT_STEP_GROUP_KEY,
  BIOSTIMULANT_STEP_GROUP_KEY,
  OPERATION_STEP_GROUP_KEY,
  PLANTPROTECTION_STEP_GROUP_KEY,
  PRODUCTION_STEP_GROUP_KEY,
  SEED_STEP_GROUP_KEY,
  SEEDDRESS_STEP_GROUP_KEY,
  SOILINPUT_STEP_GROUP_KEY,
} from 'constants/uiConstants';
import type { IUnit } from 'interfaces/IInput';
import { IGcProductGroup, InputStepType } from 'interfaces/IInputStep';

import { type StepSelectorGroup } from './stepConfig';

const UNIT_T_HA: IUnit = {
  unit: 't/ha',
  globalId: '2cf51f1f-c35f-47f8-9d8f-84670f95ed0f',
};

const UNIT_ML_100KG: IUnit = {
  unit: 'mL/100kg',
  globalId: '586f3029-3a9e-45b4-9f42-4f8a24380dfa',
};

const UNIT_HOUR: IUnit = {
  unit: 'hours',
  globalId: '670f078f-14e2-4cac-be6c-2048c6a938ba',
};

const GROUP_SEED_TREATMENT: IGcProductGroup = {
  productGroupId: '67f844f0-95e9-422f-8f31-3fbfbbf84ac8',
  snapProductGroup: 'Mixed Function Seed Treat',
};

// Array order is the picker tab order (OP-2434). Operation, Plant Protection,
// Adjuvant, Soil Input, Biostimulant, and Seed
// are sourced live from GC/MC via their respective use*Catalogue hooks
// (see StepInputSelector.tsx's isCatalogueGroup branch) — these group entries
// only carry the key/label/stepType needed to build the tab list; their
// `options` are never read.
export const STEP_SELECTOR_GROUP_DATA: StepSelectorGroup[] = [
  {
    key: SEED_STEP_GROUP_KEY,
    label: 'Seed',
    stepType: InputStepType.Seed,
    options: [],
  },
  {
    key: SEEDDRESS_STEP_GROUP_KEY,
    label: 'Seed Treatment',
    stepType: InputStepType.SeedDress,
    options: [
      {
        label: 'EverGol Prime',
        item: 'EverGol Prime',
        globalId: 'd0d9c8b7-a6f5-4e3d-8c2b-1a0f9e8d7c6b',
        rate: 0.2,
        unit: UNIT_ML_100KG,
        cost: 12.5,
        productGroups: [GROUP_SEED_TREATMENT],
        activeConstituents: [
          {
            name: 'penflufen',
            concentration: 100,
            concentrationUnit: null,
            moaGroupId: '2f8555c8-0acf-4b95-8af5-65ee3bd0238d',
            snapMoaGroup: '9B',
          },
        ],
        formulationCode: 'FS',
        formulation: 'Flowable concentrate for seed treatment',
        apvmaNumber: '99999/0001',
        poisonSchedule: 5,
        withholdPeriodGrazing: 0,
        withholdPeriodCutting: 0,
        withholdPeriodHarvest: 0,
        withholdPeriodPlantback: 0,
        reentry: 0,
        reentryUnit: UNIT_HOUR,
        reentryNotes: 'Seed treatment product. Follow label directions.',
        manufacturerId: '5254039c-3412-4af8-a0aa-42c5a8dab2b9',
        snapManufacturer: 'Nufarm Australia Ltd',
        supplierId: 'a5016ace-5633-4c7c-87b0-86aaa29d2831',
        snapSupplier: 'Nutrien Ag Solutions',
      },
      {
        label: 'Seed Shield',
        item: 'Seed Shield',
        globalId: '21bf7af7-9fff-4d0b-8af3-f56eb0786c37',
        rate: 220,
        unit: UNIT_ML_100KG,
        cost: 0.082,
        productGroups: [GROUP_SEED_TREATMENT],
        formulationCode: 'FS',
        formulation: 'Flowable Suspension',
        apvmaNumber: '80912',
        poisonSchedule: 5,
        withholdPeriodPlantback: 1,
        reentry: 8,
        reentryUnit: UNIT_HOUR,
      },
      {
        label: 'Rancona Dimension',
        item: 'Rancona Dimension',
        globalId: '99a4f2f6-5298-42c4-a2b5-6e1888587130',
        rate: 250,
        unit: UNIT_ML_100KG,
        cost: 0.095,
        productGroups: [GROUP_SEED_TREATMENT],
        formulationCode: 'FS',
        formulation: 'Flowable suspension',
        apvmaNumber: '66724',
        poisonSchedule: 5,
        withholdPeriodPlantback: 0,
        reentry: 4,
        reentryUnit: UNIT_HOUR,
      },
      {
        label: 'Vibrance',
        item: 'Vibrance',
        globalId: 'e3165598-f0e2-486f-b65f-b2b9b6bfe0cb',
        rate: 180,
        unit: UNIT_ML_100KG,
        cost: 0.089,
        productGroups: [GROUP_SEED_TREATMENT],
        formulationCode: 'FS',
        formulation: 'Flowable suspension',
        apvmaNumber: '74812',
        poisonSchedule: 5,
        withholdPeriodPlantback: 0,
        reentry: 4,
        reentryUnit: UNIT_HOUR,
      },
      {
        label: 'Systiva',
        item: 'Systiva',
        globalId: '2eec3152-5898-49fe-b8d3-f696cb3e17e8',
        rate: 145,
        unit: UNIT_ML_100KG,
        cost: 0.132,
        productGroups: [GROUP_SEED_TREATMENT],
        formulationCode: 'FS',
        formulation: 'Flowable suspension',
        apvmaNumber: '67519',
        poisonSchedule: 5,
        withholdPeriodPlantback: 0,
        reentry: 8,
        reentryUnit: UNIT_HOUR,
      },
      {
        label: 'Kinto Duo',
        item: 'Kinto Duo',
        globalId: '389ffbcb-0e28-43eb-a0f7-e39da2f1040e',
        rate: 200,
        unit: UNIT_ML_100KG,
        cost: 0.074,
        productGroups: [GROUP_SEED_TREATMENT],
        formulationCode: 'FS',
        formulation: 'Flowable suspension',
        apvmaNumber: '64581',
        poisonSchedule: 5,
        withholdPeriodPlantback: 0,
        reentry: 4,
        reentryUnit: UNIT_HOUR,
      },
    ],
  },
  {
    key: PLANTPROTECTION_STEP_GROUP_KEY,
    label: 'Plant Protection',
    stepType: InputStepType.CropProtection,
    options: [],
  },
  {
    key: ADJUVANT_STEP_GROUP_KEY,
    label: 'Adjuvant',
    stepType: InputStepType.Adjuvant,
    options: [],
  },
  {
    key: SOILINPUT_STEP_GROUP_KEY,
    label: 'Soil Input',
    stepType: InputStepType.SoilInput,
    options: [],
  },
  {
    key: OPERATION_STEP_GROUP_KEY,
    label: 'Operation',
    stepType: InputStepType.Application,
    options: [],
  },
  {
    key: BIOSTIMULANT_STEP_GROUP_KEY,
    label: 'Biostimulant',
    stepType: InputStepType.Biostimulant,
    options: [],
  },
  {
    key: PRODUCTION_STEP_GROUP_KEY,
    label: 'Production',
    stepType: InputStepType.Production,
    // Dummy grain items until the Production catalogue collection exists
    options: [
      { label: 'Grain', item: 'Grain', globalId: null },
      { label: 'Grain Pulse', item: 'Grain Pulse', globalId: null },
      { label: 'Grain Seed', item: 'Grain Seed', globalId: null },
      { label: 'Grain Stockfeed', item: 'Grain Stockfeed', globalId: null },
      {
        label: 'Harvest',
        item: 'Harvest',
        globalId: '3f0309fb-c6f1-4874-87f6-82be7c1c0ec3',
        unit: UNIT_T_HA,
        targetGrade: 'APW',
      },
      {
        label: 'Windrow',
        item: 'Windrow',
        globalId: '96b70e1c-962f-4bd2-b564-ae1529882f6c',
        unit: UNIT_T_HA,
        targetGrade: 'ASW',
      },
      {
        label: 'Grain Cart',
        item: 'Grain Cart',
        globalId: '23970a79-5e7b-48ce-9ea5-7fdd043839a7',
        unit: UNIT_T_HA,
        targetGrade: 'Feed',
      },
      {
        label: 'Direct Header',
        item: 'Direct Header',
        globalId: 'e416ed59-6d27-4599-ae1a-b0797866d30f',
        unit: UNIT_T_HA,
        targetGrade: 'H2',
      },
      {
        label: 'Swath Pickup',
        item: 'Swath Pickup',
        globalId: '7a8cdc46-e536-4a7d-a8b5-5f4a7ed18d39',
        unit: UNIT_T_HA,
        targetGrade: 'H1',
      },
      {
        label: 'On-farm Cleaning',
        item: 'On-farm Cleaning',
        globalId: 'eb2d7c35-6ad3-4c23-b4c3-f340915f8f49',
        unit: UNIT_T_HA,
        targetGrade: 'Premium',
      },
      {
        label: 'Cart to Silo',
        item: 'Cart to Silo',
        globalId: '72e3c75f-6f77-4eb0-905d-f6232f4f9254',
        unit: UNIT_T_HA,
        targetGrade: 'ASW',
      },
      {
        label: 'Bale Recovery',
        item: 'Bale Recovery',
        globalId: '75b0e38e-ec4c-45ea-9bf0-d42a3f89966f',
        unit: UNIT_T_HA,
        targetGrade: 'Utility',
      },
    ],
  },
];
